import { cloneElement, Fragment, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useForm, UseFormReset } from 'react-hook-form';
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import { z, ZodString } from 'zod';

import { Box, Button, ButtonProps, Checkbox, Flex, IconProps, InputProps, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormField, FormFields, formFieldsSchema } from '~/cms/components/form-input/form-input-model';
import { FormRadioInput } from '~/components/form-radio-input';
import { FormSelectInput } from '~/components/form-select-input';
import { FormTextInput } from '~/components/form-text-input';
import { TemplateButton } from '~/components/template-button';
import { trackEvent } from '~/features/analytics/track-event';
import { usePLPRedesignExperiment } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { SelectOption } from '~/types/select-option';

import { FormTelephoneInput } from './form-telephone-input';
import { Markdown } from './markdown';

const ArrowRightIcon = dynamic<IconProps>(() => import('@udacity/chakra-uds-icons').then((mod) => mod.ArrowRightIcon));
const ArrowRightIconVariantB = dynamic<IconProps>(() =>
  import('~/features/experiments/ab_plp_redesign_2025/icons').then((mod) => mod.ArrowRightIcon)
);

export type TemplateFormSubmitHandler = ({
  templateForm,
  formData,
  resetForm,
}: {
  templateForm: TemplateFormType;
  formData: any;
  resetForm: UseFormReset<Record<string, string | boolean | SelectOption>>;
}) => void;

export function TemplateForm({
  templateForm,
  additionalFormFields,
  onSubmit,
  submitButtonProps,
  inputStyleProps,
  defaultValues,
  onCancelClick,
  isFullWidth,
  isDisabled,
  isSubmitting,
  preventCustomUrlNavigation,
  formFieldElementsWrapper,
  successMessageSlot = null,
  renderFormFields,
}: {
  templateForm: TemplateFormType;
  additionalFormFields?: FormFields;
  onSubmit?: TemplateFormSubmitHandler;
  submitButtonProps?: ButtonProps;
  inputStyleProps?: InputProps;
  defaultValues?: Record<string, string | boolean | SelectOption>;
  onCancelClick?: () => void;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  isSubmitting?: boolean;
  preventCustomUrlNavigation?: boolean;
  formFieldElementsWrapper?: ReactElement;
  successMessageSlot?: ReactNode;
  renderFormFields?: (formFields: ReactNode[]) => ReactNode;
}) {
  const isPLPRedesignExperiment = usePLPRedesignExperiment() === 'variant_b';
  const formFields = useMemo<FormFields>(() => {
    return additionalFormFields
      ? [...translateFormDataToFormFields(templateForm.formData), ...additionalFormFields]
      : translateFormDataToFormFields(templateForm.formData);
  }, [templateForm.formData, additionalFormFields]);

  const formDataSchema = useMemo(() => generateFormSchema(formFields), [formFields]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    resolver: zodResolver(formDataSchema),
    defaultValues,
  });

  async function handleFormSubmit(formData: any) {
    // Used for cases where the component needs to handle the resulting navigation
    // TODO - Remove if this doesn't fix the Marketo issue
    if (preventCustomUrlNavigation) {
      onSubmit?.({ templateForm, formData, resetForm });
      return;
    }

    // Ensure form passes validation before navigating if using a custom url
    if (templateForm?.submitButton?.buttonAction === 'Custom URL' && templateForm?.submitButton?.customUrl) {
      window.open(templateForm.submitButton.customUrl, '_blank');
    }

    onSubmit?.({ templateForm, formData, resetForm });
  }

  const [trueConditionals, setTrueConditionals] = useState<string[]>([]);

  const fieldsToWatch = useMemo<string[]>(() => {
    let fields: string[] = [];

    for (const formField of formFields) {
      if (formField.conditional) {
        fields.push(formField.conditional);
      }
    }

    return fields;
  }, [formFields]);

  // Watch fields to determine if any conditional fields should be shown
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name && fieldsToWatch.includes(name)) {
        // If the field is now true, add it to the list of true conditionals
        if (Boolean(value[name]) && !trueConditionals.includes(name)) {
          setTrueConditionals([...trueConditionals, name]);
        }
        // If the field is no longer true, remove it from the list of true conditionals
        else if (!Boolean(value[name]) && trueConditionals.includes(name)) {
          const newTrueConditionals = trueConditionals.filter((trueConditional) => trueConditional !== name);
          setTrueConditionals(newTrueConditionals);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [fieldsToWatch, trueConditionals, watch]);

  const formFieldElements = useMemo<ReactNode[]>(() => {
    return formFields.map((formField) => {
      // If the field is conditional, only show it if the conditional is true
      if (formField.conditional) {
        if (!trueConditionals.includes(formField.conditional)) {
          return null;
        }
      }

      const label = formField.required ? `${formField.label}*` : formField.label;

      return (
        <Fragment key={formField.name}>
          {formField.inputType === 'select' && (
            <FormSelectInput
              control={control}
              controlBorderColor={inputStyleProps?.borderColor as string | undefined}
              errorMessage={errors[formField.name]?.message}
              label={label}
              name={formField.name}
              options={formField.selectOptions}
              placeholder={formField.placeholder ?? ''}
              selectProps={isDisabled ? { isDisabled: isDisabled } : undefined}
            />
          )}

          {(formField.inputType === 'text' ||
            formField.inputType === 'email' ||
            formField.inputType === 'date' ||
            formField.inputType === 'linkedin-profile-url') && (
            <FormTextInput
              errorMessage={errors[formField.name]?.message}
              isDisabled={isDisabled}
              label={label}
              placeholder={formField.placeholder ?? ''}
              type={formField.inputType}
              {...register(formField.name)}
              {...inputStyleProps}
            />
          )}

          {formField.inputType === 'tel' && (
            <FormTelephoneInput
              control={control}
              errorMessage={errors[formField.name]?.message}
              isDisabled={isDisabled}
              label={label}
              name={formField.name}
              placeholder={formField.placeholder ?? ''}
              type={formField.inputType}
            />
          )}

          {formField.inputType === 'checkbox' && (
            <Checkbox size='lg' {...register(formField.name)} isDisabled={isDisabled}>
              {label}
            </Checkbox>
          )}

          {formField.inputType === 'radio' && (
            <FormRadioInput
              errorMessage={errors[formField.name]?.message}
              isDisabled={isDisabled}
              label={label}
              name={formField.name}
              options={formField.options}
              register={register}
            />
          )}
        </Fragment>
      );
    });
  }, [control, errors, formFields, register, trueConditionals, isDisabled, inputStyleProps]);

  const renderedFormFields = useMemo<ReactNode>(() => {
    if (!formFieldElementsWrapper)
      return (
        <>
          <Stack spacing='24px'>{renderFormFields ? renderFormFields(formFieldElements) : formFieldElements}</Stack>
          <Box h='40px' />
        </>
      );
    return cloneElement(
      formFieldElementsWrapper,
      undefined,
      renderFormFields ? renderFormFields(formFieldElements) : formFieldElements
    );
  }, [formFieldElements, formFieldElementsWrapper, renderFormFields]);

  return (
    <Box>
      {successMessageSlot || (
        <>
          <Box
            as='form'
            maxWidth={isFullWidth ? '100%' : '352px'}
            width='100%'
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            {renderedFormFields}
            <Flex flexDirection={{ base: 'column-reverse', md: 'row' }} gap='16px'>
              {templateForm.cancelButton && (
                <TemplateButton
                  {...templateForm.cancelButton}
                  isDisabled={isDisabled}
                  type='submit'
                  width={{ base: '100%', md: 'fit-content' }}
                  withArrowIcon
                  onClick={() => {
                    onCancelClick?.();
                    trackEvent({
                      type: 'click',
                      name: 'Cancel Clicked',
                      label: templateForm.cancelButtonText!,
                    });
                  }}
                />
              )}

              {!templateForm.cancelButton && templateForm.cancelButtonText && (
                <Button
                  isDisabled={isDisabled}
                  variant='outline'
                  width={{ base: '100%', md: 'fit-content' }}
                  onClick={() => {
                    onCancelClick?.();
                    trackEvent({
                      type: 'click',
                      name: 'Cancel Clicked',
                      label: templateForm.cancelButtonText!,
                    });
                  }}
                >
                  {templateForm.cancelButtonText}
                </Button>
              )}

              {templateForm.submitButton ? (
                <TemplateButton
                  {...templateForm.submitButton}
                  isDisabled={isDisabled}
                  type='submit'
                  width={{ base: '100%', md: 'fit-content' }}
                  withArrowIcon
                  {...submitButtonProps}
                  buttonAction='Coded Action'
                />
              ) : (
                <Button
                  isDisabled={isDisabled}
                  isLoading={isSubmitting}
                  size='icon-right'
                  type='submit'
                  width={{ base: '100%', md: 'fit-content' }}
                  rightIcon={
                    isPLPRedesignExperiment ? (
                      <ArrowRightIconVariantB height='20px' width='20px' />
                    ) : (
                      <ArrowRightIcon h='24px' w='24px' />
                    )
                  }
                  {...submitButtonProps}
                >
                  {templateForm.submitButtonText}
                </Button>
              )}
            </Flex>
          </Box>
          {templateForm.legalText && (
            <Box className='legal-text-container' marginTop='16px'>
              <Markdown markdownText={templateForm.legalText} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function generateFormSchema(formFields: FormFields) {
  let formSchema = z.object({});

  for (const formField of formFields) {
    formSchema = formSchema.extend(getFormFieldObject(formField));
  }

  return formSchema;
}

function getFormFieldObject(formField: FormField) {
  // Handle text input
  if (formField.inputType === 'text' || formField.inputType === 'date') {
    return {
      [formField.name]: attachCommonPropertiesToStrings(z.string()),
    };
  } else if (formField.inputType === 'tel') {
    return {
      [formField.name]: attachCommonPropertiesToStrings(z.string().trim())
        .refine(
          (val) => {
            if (val) {
              return isValidPhoneNumber(val);
            } else {
              return true;
            }
          },
          {
            message: 'Please provide a valid phone number.',
          }
        )
        .transform((val) => {
          if (val) {
            return formatPhoneNumberIntl(val as any);
          }
          return val;
        }),
    };
  } else if (formField.inputType === 'linkedin-profile-url') {
    return {
      [formField.name]: attachCommonPropertiesToStrings(z.string().url())
        .transform((url) => url?.toLowerCase())
        .refine((url) => url && /^https?:\/\/(?:www\.)?linkedin\.com\/in\/[\w-]+\/?$/i.test(url), {
          message: 'Invalid url format. Example format: https://www.linkedin.com/in/username',
        }),
    };
  }

  // Handle email input
  else if (formField.inputType === 'email') {
    if (formField.disallowCommonEmails) {
      const commonEmailAddressesRegex = /^[^@]+@(?!(gmail|yahoo|aol|outlook|hotmail))[^.]+(?:\.[^.]+)+$/;

      return {
        [formField.name]: attachCommonPropertiesToStrings(z.string().email()).refine(
          (val) => val && commonEmailAddressesRegex.test(val),
          {
            message: 'Please provide a valid business email',
          }
        ),
      };
    }

    return { [formField.name]: attachCommonPropertiesToStrings(z.string().email()) };
  }

  // Handle checkbox input
  else if (formField.inputType === 'checkbox') {
    return { [formField.name]: z.boolean() };
  }
  // Handle select input
  else if (formField.inputType === 'select') {
    const options = formField.selectOptions.map((option) => {
      return z.object({
        label: z.literal(option.label),
        value: z.literal(option.value),
      });
    });

    return {
      // @ts-expect-error
      [formField.name]: z.union([...options]),
    };
  }
  // Handle radio input
  else if (formField.inputType === 'radio') {
    return {
      [formField.name]: attachCommonPropertiesToStrings(z.string({ invalid_type_error: 'This field is required.' })),
    };
  }

  // Handle unknown input type
  else {
    throw new Error(`Unknown input type`);
  }

  // Attach common properties to the string value
  function attachCommonPropertiesToStrings(zodString: ZodString) {
    let zodStringWithCommonProperties;

    if (!formField.required) {
      zodStringWithCommonProperties = zodString.optional();
    } else {
      zodStringWithCommonProperties = zodString.nonempty({ message: 'This field is required.' });
    }

    return zodStringWithCommonProperties;
  }
}

export function translateFormDataToFormFields(formData: string): FormFields {
  try {
    const maybeFormFields = JSON.parse(formData);

    let formFields = formFieldsSchema.parse(maybeFormFields);

    // Sort formFields by order
    formFields.sort((a, b) => a.order - b.order);

    return formFields;
  } catch (error) {
    throw new Error('Invalid form data');
  }
}
